import { useEffect, useState } from "react"
import {
    AppBar,
    Button,
    Box,
    Card,
    CardContent,
    Dialog,
    IconButton,
    Stack,
    Toolbar,
    Typography,
    MenuItem,
    ListItemIcon,
    ListItemText
} from "@mui/material"
import {
    Datagrid,
    DateField,
    FunctionField,
    Labeled,
    List,
    SelectInput,
    TextField,
    TextInput,
    useDataProvider,
    useListContext,
    useNotify
} from "react-admin"
import CloseIcon from '@mui/icons-material/Close';
import LocalPrintshopTwoToneIcon from '@mui/icons-material/LocalPrintshopTwoTone';
import DomainTwoToneIcon from '@mui/icons-material/DomainTwoTone';
import RocketTwoToneIcon from '@mui/icons-material/RocketTwoTone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';


import { provinces, getProvince } from "../../utils";
import restProvider from "../../../../rest.provider";
import {
    VBagLabel,
    VLabel,
    VStudentLabel,
    zBagLabel,
    zLabel,
    zStudentLabel
} from "./components";
import { OrdersPagination } from "../../components/Pagination";


export const StudentOrderList = () => {
    const [legacyPrinters, setLegacyPrinters] = useState({
        box: false,
        bag: false,
        student: false
    })
    const [orders, setOrders] = useState([])

    const [printers, setPrinters] = useState({
        box: undefined,
        bag: undefined,
        student: undefined
    })
    const [labels, setLabels] = useState(false)
    const [studentLabels, setStudentLabels] = useState(() => {
        const settings = JSON.parse(localStorage.getItem('zWarehouse')) || {}
        return settings.studentLabels || 40
    })
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const dataProvider = useDataProvider()
    const [releaseDates, setReleaseDates] = useState([])

    const notify = useNotify()

    // Function to get printer selections from localStorage (same as Settings and Custom Labels)
    const getPrinterSelections = () => {
        try {
            const stored = localStorage.getItem('zWarehouse_printer_selections');
            const selections = stored ? JSON.parse(stored) : {};
            const defaultSelections = { box: null, bag: null, student: null };
            return { ...defaultSelections, ...selections };
        } catch (error) {
            console.error('Error loading printer selections:', error);
            return { box: null, bag: null, student: null };
        }
    };

    const releaseDateOptions = releaseDates
        .map(item => Object.values(item)[0])
        .filter(value => value !== '')
        .map(value => ({ id: value, name: value }))

    const Filters = [
        <TextInput source='batch' />,
        <TextInput label='Grade' source='grade' />,
        <TextInput label='School' source='school' />,
        <TextInput label='City' source='city' />,
        <SelectInput
            source='province'
            optionText='province'
            optionValue='province'
            choices={provinces} />,
        <SelectInput
            label='Release Date'
            source='release'
            choices={releaseDateOptions} />
    ]
    
    const sort = { field: 'batch', order: 'DESC' }

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
    }

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both the button and the dropdown
            const buttonElement = menuAnchorEl
            const dropdownElement = document.querySelector('[data-dropdown="print-labels"]')
            
            if (buttonElement && 
                !buttonElement.contains(event.target) && 
                !dropdownElement?.contains(event.target)) {
                handleMenuClose()
            }
        }

        if (Boolean(menuAnchorEl)) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [menuAnchorEl])

    useEffect(() => {
        const settings = JSON.parse(localStorage.getItem('zWarehouse'))
        const { printers, studentLabels } = settings
        const { printer } = window.devices

        if (printer) {
            setLegacyPrinters({
                box: printer.find(p => p.uid === printers['box']?.uid),
                bag: printer.find(p => p.uid === printers['bag']?.uid),
                student: printer.find(p => p.uid === printers['student']?.uid)
            })
        }

        if (studentLabels) {
            setStudentLabels(studentLabels)
        }
    }, [])


    const handlePreview = (labelType) => {
        setLabels(labelType)
        handleMenuClose()
    }

    const handleLegacyPrint = (labels, selectedIds, data) => {

        let printData = ''
        let selected = []
        selectedIds?.forEach(id => {
            selected = [...selected, data.find(item => item.id === id)]
        })

        switch (labels) {
            case 'box':
                selected.forEach(order => {
                    printData = printData + zLabel(order)
                })

                legacyPrinters?.box.send(printData)
                break

            case 'bag':
                selected.forEach(order => {
                    printData = printData + zBagLabel(order)
                })
                
                legacyPrinters?.bag.send(printData)
                break

            case 'student':
                orders.forEach(order => {
                    printData = Array.from(Array(+studentLabels).keys()).map(_ => {
                        return printData + '^XA' + zStudentLabel(order) + '^XZ'
                    })
                })

                try {
                    legacyPrinters.student.send(printData)
                } catch (e) {
                    console.error("Oops!", e)
                    notify('Unable to print!  Printer not responding', { type: 'error' })
                }

                break

            default:
                break                    
        }
    }

    const handleDirectPrint = async (labelType, contextData) => {
        handleMenuClose()
        
        // Use passed context data
        const { data, selectedIds } = contextData
        
        // Debug logging to understand the context data
        console.log('Direct Print Debug:', { 
            dataLength: data?.length, 
            selectedIdsLength: selectedIds?.length, 
            selectedIds, 
            data: data?.slice(0, 2) // First 2 items for debugging
        })
        
        // Get selected orders from context data
        const orders = data ? selectedIds.map(id => data.find(item => item.id === id)).filter(Boolean) : []
        
        console.log('Orders found:', orders.length, orders.slice(0, 2))
        
        if (orders.length === 0) {
            notify('No orders selected', { type: 'warning' })
            return
        }

        // Execute print logic directly
        let printData = ''

        switch (labelType) {
            case 'box':
                // Use API printer if available, otherwise fall back to legacy printer
                if (printers.box) {
                    try {
                        // Generate ZPL data for box labels
                        let zplData = ''
                        orders.forEach(order => {
                            zplData += zLabel(order, releaseDates)
                        })

                        // Send to printer API
                        const printResult = await restProvider.printLabel('printer', {
                            labelData: {
                                zpl: zplData
                            },
                            printer: printers.box.ip
                        })

                        if (printResult.data && printResult.data.success) {
                            const printerName = printers.box.name || printers.box.ip
                            const printerLocation = printers.box.location ? ` (${printers.box.location})` : ''
                            notify(`Box labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                        } else {
                            const errorMessage = printResult.data?.message || 'Failed to print box labels via API'
                            notify(errorMessage, { type: 'error' })
                        }
                    } catch (error) {
                        console.error('API print error:', error)
                        notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                        
                        // Fall back to legacy printer
                        try {
                            let legacyData = ''
                            orders.forEach(order => {
                                legacyData += zLabel(order, releaseDates)
                            })
                            printers.box.send(legacyData)
                            notify('Printed via legacy printer', { type: 'success' })
                        } catch (legacyError) {
                            console.error("Legacy printer error:", legacyError)
                            notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                        }
                    }
                } else {
                    // Use legacy printer
                    try {
                        orders.forEach(order => {
                            printData += zLabel(order, releaseDates)
                        })
                        printers.box.send(printData)
                        notify('Printed via legacy printer', { type: 'success' })
                    } catch(e) {
                        console.error("Legacy printer error:", e)
                        notify('Unable to print!  Printer not responding', { type: 'error' })
                    }
                }

            case 'bag':
                // Use API printer if available, otherwise fall back to legacy printer
                if (printers.bag) {
                    try {
                        // Generate ZPL data for bag labels
                        let zplData = ''
                        orders.forEach(order => {
                            zplData += zBagLabel(order)
                        })

                        // Send to printer API
                        const printResult = await restProvider.printLabel('printer', {
                            labelData: {
                                zpl: zplData
                            },
                            printer: printers.bag.ip
                        })

                        if (printResult.data && printResult.data.success) {
                            const printerName = printers.bag.name || printers.bag.ip
                            const printerLocation = printers.bag.location ? ` (${printers.bag.location})` : ''
                            notify(`Bag labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                        } else {
                            const errorMessage = printResult.data?.message || 'Failed to print bag labels via API'
                            notify(errorMessage, { type: 'error' })
                        }
                    } catch (error) {
                        console.error('API print error:', error)
                        notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                        
                        // Fall back to legacy printer
                        try {
                            let legacyData = ''
                            orders.forEach(order => {
                                legacyData += zBagLabel(order)
                            })
                            printers.bag.send(legacyData)
                            notify('Printed via legacy printer', { type: 'success' })
                        } catch (legacyError) {
                            console.error("Legacy printer error:", legacyError)
                            notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                        }
                    }
                } else {
                    // Use legacy printer
                    try {
                        orders.forEach(order => {
                            printData += zBagLabel(order)
                        })
                        printers.bag.send(printData)
                        notify('Printed via legacy printer', { type: 'success' })
                    } catch(e) {
                        console.error("Legacy printer error:", e)
                        notify('Unable to print!  Printer not responding', { type: 'error' })
                    }
                }
                break

            case 'student':
                // Get current printer selections from localStorage
                const printerSelections = getPrinterSelections();
                const studentPrinter = printerSelections.student;
                
                // Use API printer if available, otherwise fall back to legacy printer
                if (studentPrinter && studentPrinter.ip) {
                    try {
                        // Print each order's student labels using the student_labels format
                        for (const order of orders) {
                            const studentName = order.student || 'Student'
                            
                            // Send to printer API using student_labels format
                            const printResult = await restProvider.printLabel('printer', {
                                labelType: 'student_labels',
                                labelData: {
                                    studentName: studentName,
                                    numLabels: +studentLabels,
                                    defaultFontSize: 56,
                                    abbreviateLongNames: true
                                },
                                printer: studentPrinter.ip
                            })

                            if (!printResult.data || !printResult.data.success) {
                                const errorMessage = printResult.data?.message || 'Failed to print student labels via API'
                                notify(errorMessage, { type: 'error' })
                                break // Stop printing if one fails
                            }
                        }

                        const printerName = studentPrinter.name || studentPrinter.ip
                        const printerLocation = studentPrinter.location ? ` (${studentPrinter.location})` : ''
                        notify(`Student labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                    } catch (error) {
                        console.error('API print error:', error)
                        notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                        
                        // Fall back to legacy printer
                        try {
                            let legacyData = ''
                            orders.forEach(order => {
                                for (let i = 0; i < +studentLabels; i++) {
                                    legacyData += '^XA' + zStudentLabel(order) + '^XZ'
                                }
                            })
                            printers.student.send(legacyData)
                            notify('Student labels printed via legacy printer', { type: 'success' })
                        } catch (legacyError) {
                            console.error("Legacy printer error:", legacyError)
                            notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                        }
                    }
                } else {
                    // Use legacy printer
                    try {
                        orders.forEach(order => {
                            for (let i = 0; i < +studentLabels; i++) {
                                printData += '^XA' + zStudentLabel(order) + '^XZ'
                            }
                        })
                        printers?.student.send(printData)
                        notify('Student labels printed via legacy printer', { type: 'success' })
                    } catch(e) {
                        console.error("Legacy printer error:", e)
                        notify('Unable to print!  Printer not responding', { type: 'error'})
                    }
                }
                break

            default:
                break
        }
    }

        const Actions = () => {
        // Get context data inside the Actions component
        const { data, selectedIds } = useListContext()
        
        return (
            <>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Button
                        variant='outlined'
                        onClick={handleMenuOpen}
                        endIcon={<MoreVertIcon />}
                    >
                        Print Labels
                    </Button>
                    {Boolean(menuAnchorEl) && (
                    <Box
                        data-dropdown="print-labels"
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            zIndex: 1300,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            boxShadow: 3,
                            minWidth: 200,
                            display: 'block',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* <MenuItem onClick={() => handlePreview('box')}>
                            <ListItemIcon>
                                <VisibilityIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Preview Box Labels</ListItemText>
                        </MenuItem> */}
                        <MenuItem onClick={() => handleLegacyPrint('box', selectedIds, data)}>
                            <ListItemIcon>
                                <PrintIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Print Box Labels</ListItemText>
                        </MenuItem>
                        {/* <MenuItem onClick={() => handlePreview('bag')}>
                            <ListItemIcon>
                                <VisibilityIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Preview Bag Labels</ListItemText>
                        </MenuItem> */}
                        <MenuItem onClick={() => handleLegacyPrint('bag', selectedIds, data)}>
                            <ListItemIcon>
                                <PrintIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Print Bag Labels</ListItemText>
                        </MenuItem>
                        {/* <MenuItem onClick={() => handlePreview('student')}>
                            <ListItemIcon>
                                <VisibilityIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Preview Student Labels</ListItemText>
                        </MenuItem> */}
                        <MenuItem onClick={() => handleDirectPrint('student', { data, selectedIds })}>
                            <ListItemIcon>
                                <PrintIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Print Student Labels</ListItemText>
                        </MenuItem>
                    </Box>
                )}
                </Box>
            </>
        )
    }

    const Preview = () => {
        const { data, selectedIds } = useListContext()
        const [orders, setOrders] = useState([])

        const handlePrint = async () => {
            // let data = ''

            switch (labels) {
                case 'box':
                    handleLegacyPrint('box', selectedIds, data)

                    // Use API printer if available, otherwise fall back to legacy printer
                    // if (printers.box) {
                    //     try {
                    //         // Generate ZPL data for box labels
                    //         let zplData = ''
                    //         orders.forEach(order => {
                    //             zplData += zLabel(order, releaseDates)
                    //         })

                    //         // Send to printer API
                    //         const printResult = await restProvider.printLabel('printer', {
                    //             labelData: {
                    //                 zpl: zplData
                    //             },
                    //             printer: printers.box.ip
                    //         })

                    //         if (printResult.data && printResult.data.success) {
                    //             const printerName = printers.box.name || printers.box.ip
                    //             const printerLocation = printers.box.location ? ` (${printers.box.location})` : ''
                    //             notify(`Box labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                    //             setLabels(false) // Close the modal after successful print
                    //         } else {
                    //             const errorMessage = printResult.data?.message || 'Failed to print box labels via API'
                    //             notify(errorMessage, { type: 'error' })
                    //         }
                    //     } catch (error) {
                    //         console.error('API print error:', error)
                    //         notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                            
                    //         // Fall back to legacy printer
                    //         try {
                    //             let legacyData = ''
                    //             orders.forEach(order => {
                    //                 legacyData += zLabel(order, releaseDates)
                    //             })
                    //             printers.box.send(legacyData)
                    //             notify('Printed via legacy printer', { type: 'success' })
                    //             setLabels(false) // Close the modal after successful print
                    //         } catch (legacyError) {
                    //             console.error("Legacy printer error:", legacyError)
                    //             notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                    //         }
                    //     }
                    // } else {
                    //     // Use legacy printer
                    //     try {
                    //         orders.forEach(order => {
                    //             data += zLabel(order, releaseDates)
                    //         })
                    //         printers.box.send(data)
                    //         notify('Printed via legacy printer', { type: 'success' })
                    //         setLabels(false) // Close the modal after successful print
                    //     } catch(e) {
                    //         console.error("Legacy printer error:", e)
                    //         notify('Unable to print!  Printer not responding', { type: 'error' })
                    //     }
                    // }
                    // break

                case 'bag':

                    // Use API printer if available, otherwise fall back to legacy printer
                    if (printers.bag) {
                        try {
                            // Generate ZPL data for bag labels
                            let zplData = ''
                            orders.forEach(order => {
                                zplData += zBagLabel(order)
                            })

                            // Send to printer API
                            const printResult = await restProvider.printLabel('printer', {
                                labelData: {
                                    zpl: zplData
                                },
                                printer: printers.bag.ip
                            })

                            if (printResult.data && printResult.data.success) {
                                const printerName = printers.bag.name || printers.bag.ip
                                const printerLocation = printers.bag.location ? ` (${printers.bag.location})` : ''
                                notify(`Bag labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                                setLabels(false) // Close the modal after successful print
                            } else {
                                const errorMessage = printResult.data?.message || 'Failed to print bag labels via API'
                                notify(errorMessage, { type: 'error' })
                            }
                        } catch (error) {
                            console.error('API print error:', error)
                            notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                            
                            // Fall back to legacy printer
                            try {
                                let legacyData = ''
                                orders.forEach(order => {
                                    legacyData += zBagLabel(order)
                                })
                                printers.bag.send(legacyData)
                                notify('Printed via legacy printer', { type: 'success' })
                                setLabels(false) // Close the modal after successful print
                            } catch (legacyError) {
                                console.error("Legacy printer error:", legacyError)
                                notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                            }
                        }
                    } else {
                        // Use legacy printer
                        try {
                            orders.forEach(order => {
                                data += zBagLabel(order)
                            })
                            printers.bag.send(data)
                            notify('Printed via legacy printer', { type: 'success' })
                            setLabels(false) // Close the modal after successful print
                        } catch(e) {
                            console.error("Legacy printer error:", e)
                            notify('Unable to print!  Printer not responding', { type: 'error' })
                        }
                    }
                    break

                case 'student':

                    // Get current printer selections from localStorage
                    const printerSelections = getPrinterSelections();
                    const studentPrinter = printerSelections.student;

                    // Use API printer if available, otherwise fall back to legacy printer
                    if (studentPrinter && studentPrinter.ip) {
                        try {
                            // Print each order's student labels using the student_labels format
                            for (const order of orders) {
                                const studentName = order.student || 'Student'
                                
                                // Send to printer API using student_labels format
                                const printResult = await restProvider.printLabel('printer', {
                                    labelType: 'student_labels',
                                    labelData: {
                                        studentName: studentName,
                                        numLabels: +studentLabels,
                                        defaultFontSize: 56,
                                        abbreviateLongNames: true
                                    },
                                    printer: studentPrinter.ip
                                })

                                if (!printResult.data || !printResult.data.success) {
                                    const errorMessage = printResult.data?.message || 'Failed to print student labels via API'
                                    notify(errorMessage, { type: 'error' })
                                    break // Stop printing if one fails
                                }
                            }

                            const printerName = studentPrinter.name || studentPrinter.ip
                            const printerLocation = studentPrinter.location ? ` (${studentPrinter.location})` : ''
                            notify(`Student labels printed successfully to ${printerName}${printerLocation}`, { type: 'success' })
                            setLabels(false) // Close the modal after successful print
                        } catch (error) {
                            console.error('API print error:', error)
                            notify('Failed to print via API, falling back to legacy printer', { type: 'warning' })
                            
                            // Fall back to legacy printer
                            try {
                                let legacyData = ''
                                orders.forEach(order => {
                                    for (let i = 0; i < +studentLabels; i++) {
                                        legacyData += '^XA' + zStudentLabel(order) + '^XZ'
                                    }
                                })
                                printers.student.send(legacyData)
                                notify('Student labels printed via legacy printer', { type: 'success' })
                                setLabels(false) // Close the modal after successful print
                            } catch (legacyError) {
                                console.error("Legacy printer error:", legacyError)
                                notify('Unable to print!  Both API and legacy printers failed', { type: 'error' })
                                setLabels(false) // Close the modal even on error
                            }
                        }
                    } else {
                        // Use legacy printer
                        try {
                            orders.forEach(order => {
                                for (let i = 0; i < +studentLabels; i++) {
                                    data += '^XA' + zStudentLabel(order) + '^XZ'
                                }
                            })
                            printers?.student.send(data)
                            notify('Student labels printed via legacy printer', { type: 'success' })
                            setLabels(false) // Close the modal after successful print
                        } catch(e) {
                            console.error("Legacy printer error:", e)
                            notify('Unable to print!  Printer not responding', { type: 'error'})
                            setLabels(false) // Close the modal even on error
                        }
                    }

                    break

                default:
                    break                    
            }
        }
 
        useEffect(() => {
            let selected = []
            if (data) {
                selectedIds.forEach(id => {
                    selected = [...selected, data.find(item => item.id === id)]
                })
                setOrders(selected)
            }

        }, [data, selectedIds])

        return (
            <Dialog open={labels ? true : false} onClose={()=>setLabels()} fullWidth>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar sx={{ justifyContent: 'space-between'}}>
                        <Typography sx={{ ml: 2, flex: 1 }} variant='h5' component='div'>
                            { labels === 'box' && 'Box Labels (preview)' }
                            { labels === 'bag' && 'Bag Labels (preview)' }
                            { labels === 'student' && `Student Labels (x${studentLabels})` }
                        </Typography>

                        <Stack direction='row' spacing={1}>
                            <IconButton
                                color='inherit'
                                onClick={handlePrint}>
                                <LocalPrintshopTwoToneIcon fontSize='large' />
                            </IconButton>
                            
                            <IconButton
                                color='inherit'
                                onClick={() => setLabels()}
                                aria-label="close">
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                    </Toolbar>
                </AppBar>

                <Box>
                    {labels === 'box' && orders && orders.map((order, idx) =>
                        <VLabel
                            key={idx}
                            order={order}
                            releaseDates={releaseDates} 
                        />                    )}
                    {labels === 'bag' && orders && orders.map((order, idx) =>
                        <VBagLabel order={order} key={idx} />
                    )}
                    {labels === 'student' && orders && orders.map((order, idx) =>
                        <VStudentLabel order={order} key={idx} />
                    )}
                </Box>
            </Dialog>
        )
    }

    useEffect(() => {
        const settings = JSON.parse(localStorage.getItem('zWarehouse'))
        const { printers, studentLabels } = settings
        const { printer } = window.devices

        dataProvider.getConstants()
            .then(({ data }) => {
                const dates = data.filter(constant => {
                    const item = Object.keys(constant)[0]
                    return item.startsWith('release_date')
                })

                setReleaseDates(dates)
            })

        if (printer) {
            setPrinters({
                box: printer.find(p => p.uid === printers['box']?.uid),
                bag: printer.find(p => p.uid === printers['bag']?.uid),
                student: printer.find(p => p.uid === printers['student']?.uid)
            })
        }

        if (studentLabels) {
            setStudentLabels(studentLabels)
        }
    }, [dataProvider])

    return (
        <Card>
            <CardContent>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <DomainTwoToneIcon sx={{ fontSize: 46 }} color="primary" />
                    <Typography variant='h4' color='primary'>
                        School Delivery
                    </Typography>
                    <Typography variant='h5' color='primary'>Student Orders 'On Deck'</Typography>
                    <RocketTwoToneIcon color='secondary' />
                </Stack>
                <List filters={Filters} sort={sort} pagination={<OrdersPagination />}>
                    <>
                        <Preview />
                        <Datagrid bulkActionButtons={<Actions />}>
                            <TextField source='batch' />

                            <FunctionField label='ID' render={record =>
                                `K-${record.orderId}-${record.id}`} />

                            <Labeled label='Order Date'>
                                <DateField source='order.date' label={false} />
                            </Labeled>

                            <TextField source='school' />
                            <TextField source='city' />

                            <FunctionField render={record =>
                                getProvince(record.school_address.province).code} />

                            <TextField source='student' />
                            <TextField source='kit.name' />
                            <TextField source='release_date' />
                        </Datagrid>
                    </>
                </List>
            </CardContent>
        </Card>
    )
}